/**
 * Event destinations—the additional locations an event fans out to beyond the
 * universal Ekho-internal baseline. Every event that flows through an adapter
 * lands in the Ekho-internal CDP (Segment → BigQuery + the Segment→PostHog
 * forward); that baseline is unconditional and is not modeled as a destination.
 * `destinations` enumerates only the extra, side-effectful fan-out legs a given
 * event triggers (dealer dataLayer pushes, ad-platform pixels, server-side
 * conversion uploads). Routing is additive membership; there are no standalone
 * routing booleans.
 *
 * Lives in its own module (rather than `events/index.ts`) so the event-group
 * files (`funnel.ts`, `engagement.ts`, …) can reference destinations without
 * an import cycle through the registry barrel.
 */
/** Fan-out legs beyond the Ekho-internal CDP baseline an event lands in. */
const EVENT_DESTINATION = {
    /**
     * The dealer's own GTM dataLayer. The dataLayer leg only fires when the
     * event has an `adEventNames.gtm` name to push under.
     */
    GtmDataLayer: 'GTM_DATALAYER',
    /**
     * Browser-side ad fan-out: `ConversionTracking` direct pixels
     * (Meta/TikTok/Tracklution/gtag) + the dealer dataLayer ecommerce push. The
     * web adapter serves this; per-tenant marketing resources pick the platforms.
     */
    ClientAdFanout: 'CLIENT_AD_FANOUT',
    /**
     * Server-side ad fan-out: `ServerAnalytics` publishes to the `conversion.event`
     * Pub/Sub topic, which the ad-adapter registry consumes (Google Ads Enhanced
     * Conversions for Leads today; Meta CAPI / TikTok Events API later). Per-tenant
     * tracking config picks the platforms.
     */
    ServerAdFanout: 'SERVER_AD_FANOUT',
};
/**
 * Client ad-fanout events: the dealer dataLayer (GA4 ecommerce/custom push via
 * ConversionTracking) + direct browser pixels. The CDP baseline is implicit.
 */
const CLIENT_AD_EVENT_DESTINATIONS = [
    EVENT_DESTINATION.GtmDataLayer,
    EVENT_DESTINATION.ClientAdFanout,
];
/**
 * Server ad-fanout events (conversion milestones): the server adapter's
 * `conversion.event` Pub/Sub fan-out. The CDP baseline is implicit.
 */
const SERVER_AD_EVENT_DESTINATIONS = [EVENT_DESTINATION.ServerAdFanout];
/**
 * A plain (non-ecommerce, non-pixel) push to the dealer dataLayer under the
 * event's `adEventNames.gtm` name. The CDP baseline is implicit.
 */
const DEALER_DATALAYER_DESTINATIONS = [EVENT_DESTINATION.GtmDataLayer];

export { CLIENT_AD_EVENT_DESTINATIONS, DEALER_DATALAYER_DESTINATIONS, EVENT_DESTINATION, SERVER_AD_EVENT_DESTINATIONS };
//# sourceMappingURL=destinations.js.map
